import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSearch } from 'react-icons/fa';
import axios from 'axios';

const Stationer = () => {
  const [stationer, setStationer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const pageSizeOptions = [20, 50, 100, 200];
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const isAdmin = (() => {
    try { return localStorage.getItem('userRole') === 'admin'; } catch (_) { return false; }
  })();

  const [formData, setFormData] = useState({
    stationsnummer: '',
    navn: '',
    adresse: '',
    stationsleder: '',
    telefonnummer: '',
    doerkode: ''
  });

  useEffect(() => {
    fetchStationer();
  }, []);

  const fetchStationer = async () => {
    try {
      const response = await axios.get('/api/stationer');
      setStationer(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stationer:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStation) {
        await axios.put(`/api/stationer/${editingStation.id}`, formData);
      } else {
        await axios.post('/api/stationer', formData);
      }
      setShowModal(false);
      setEditingStation(null);
      resetForm();
      fetchStationer();
    } catch (error) {
      console.error('Error saving station:', error);
    }
  };

  const handleEdit = (station) => {
    setEditingStation(station);
    setFormData({
      stationsnummer: station.stationsnummer || '',
      navn: station.navn,
      adresse: station.adresse,
      stationsleder: station.stationsleder || '',
      telefonnummer: station.telefonnummer || '',
      doerkode: station.doerkode || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Er du sikker på, at du vil slette denne station?')) {
      try {
        await axios.delete(`/api/stationer/${id}`);
        fetchStationer();
      } catch (error) {
        console.error('Error deleting station:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      stationsnummer: '',
      navn: '',
      adresse: '',
      stationsleder: '',
      telefonnummer: '',
      doerkode: ''
    });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const filteredStationer = useMemo(() => {
    const searchValue = searchTerm.trim().toLowerCase();
    return stationer.filter(station => {
      const navnMatch = station.navn && station.navn.toLowerCase().includes(searchValue);
      const adresseMatch = station.adresse && station.adresse.toLowerCase().includes(searchValue);
      const lederMatch = station.stationsleder && station.stationsleder.toLowerCase().includes(searchValue);
      return !searchValue || navnMatch || adresseMatch || lederMatch;
    });
  }, [stationer, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredStationer.length / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedStationer = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredStationer.slice(start, start + pageSize);
  }, [filteredStationer, currentPage, pageSize]);

  const hasResults = filteredStationer.length > 0;
  const startItem = hasResults ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = hasResults ? startItem + paginatedStationer.length - 1 : 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Stationer</h1>
        {isAdmin && (
          <button
            onClick={() => {
              setEditingStation(null);
              resetForm();
              setShowModal(true);
            }}
            className="bg-falck-blue text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
          >
            <FaPlus /> Tilføj ny station
          </button>
        )}
      </div>

      {/* Søgefelt */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 rounded-lg shadow mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Søg efter station..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-falck-blue"
            />
          </div>
          <div className="text-sm text-gray-600">
            {filteredStationer.length} af {stationer.length} stationer
          </div>
        </div>
      </div>

      {/* Stationer tabel */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow overflow-hidden">
        <div className="scroll-fade-x"><div className="scroll-area">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Navn
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Adresse
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Stationsleder
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Telefonnummer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Dørkode
                </th>
                {isAdmin && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Handlinger
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedStationer.map((station) => (
                <tr key={station.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/stationer/profil/${station.id}`)}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {station.navn}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {station.adresse}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {station.stationsleder || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {station.telefonnummer || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {station.doerkode || '-'}
                  </td>
                  {isAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/stationer/profil/${station.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Se profil"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handleEdit(station)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Rediger"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(station.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Slet"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40">
          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <span>Vis</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="px-2 py-1 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-falck-blue bg-white dark:bg-gray-800"
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <span>pr. side</span>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            {hasResults
              ? `Viser ${startItem}-${endItem} af ${filteredStationer.length} stationer (total ${stationer.length})`
              : `Ingen stationer matcher søgningen (total ${stationer.length})`}
          </div>
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 sm:gap-3 text-xs sm:text-sm text-gray-700 dark:text-gray-200">
            {currentPage > 1 && hasResults && (
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                Første
              </button>
            )}
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || !hasResults}
              className={`px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 transition ${currentPage === 1 || !hasResults ? 'opacity-60 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              Forrige
            </button>
            <span className="whitespace-nowrap">Side {hasResults ? currentPage : 0} af {hasResults ? totalPages : 0}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages || !hasResults}
              className={`px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 transition ${currentPage === totalPages || !hasResults ? 'opacity-60 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              Næste
            </button>
            {currentPage < totalPages && hasResults && (
              <button
                type="button"
                onClick={() => setCurrentPage(totalPages)}
                className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                Sidste
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-gray-600/60 px-3 sm:px-4 py-6 overflow-y-auto">
            <div className="w-full max-w-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
              <div className="mt-1">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                {editingStation ? 'Rediger station' : 'Tilføj ny station'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Stationsnummer *</label>
                  <input
                    type="text"
                    required
                    value={formData.stationsnummer}
                    onChange={(e) => setFormData({ ...formData, stationsnummer: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Navn *</label>
                  <input
                    type="text"
                    required
                    value={formData.navn}
                    onChange={(e) => setFormData({...formData, navn: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Adresse *</label>
                  <input
                    type="text"
                    required
                    value={formData.adresse}
                    onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Stationsleder</label>
                  <input
                    type="text"
                    value={formData.stationsleder}
                    onChange={(e) => setFormData({...formData, stationsleder: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Telefonnummer</label>
                  <input
                    type="text"
                    value={formData.telefonnummer}
                    onChange={(e) => setFormData({...formData, telefonnummer: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Dørkode</label>
                  <input
                    type="text"
                    value={formData.doerkode}
                    onChange={(e) => setFormData({...formData, doerkode: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                </div>
                  <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingStation(null);
                      resetForm();
                    }}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-800 rounded-md hover:bg-gray-300 dark:hover:bg-gray-700"
                  >
                    Annuller
                  </button>
                  <button
                    type="submit"
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-falck-blue rounded-md hover:bg-blue-700"
                  >
                    {editingStation ? 'Opdater' : 'Opret'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stationer;
